import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { EducationalPathUserDTO } from '../../../dto/educational-path/educational-path-user.dto';
import {
  UserProgressTotalsInput,
  UserProgressResolveOptions,
  buildPathBreakdownRows,
  formatMinutesShort,
  resolveProgressMinuteTotals,
} from './duna-user-progress-breakdown.util';

@Component({
  selector: 'app-duna-user-progress-label',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './duna-user-progress-label.component.html',
  styleUrl: './duna-user-progress-label.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaUserProgressLabelComponent implements OnChanges, OnDestroy {
  @Input() registeredPercentage: number | null = 0;
  @Input() progressPercentage: number | null = 0;
  @Input() paths: EducationalPathUserDTO[] | null = null;
  @Input() totals: UserProgressTotalsInput | null = null;
  @Input() year: number | null = null;
  @Input() compact = false;
  @Input() showBar = true;
  @Input() showBreakdown = true;
  @Input() aggregateContext: 'location' | 'user' | 'default' = 'default';
  @Input() aggregateTooltip = 'Summeret på tværs af alle brugere på stationen';
  /** Respects org setting "Moduler tælles kun én gang" for tilmeldt-minutter. */
  @Input() countModuleOnce = false;

  @ViewChild('breakdownPanel') breakdownPanelTemplate!: TemplateRef<unknown>;

  readonly breakdownOpen = signal(false);

  private readonly pathsSignal = signal<EducationalPathUserDTO[] | null>(null);
  private readonly totalsSignal = signal<UserProgressTotalsInput | null>(null);
  private readonly registeredSignal = signal<number | null>(0);
  private readonly progressSignal = signal<number | null>(0);
  private readonly countModuleOnceSignal = signal(false);

  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private overlayRef: OverlayRef | null = null;
  private readonly onOverlayMouseEnter = (): void => this.cancelCloseTimer();
  private readonly onOverlayMouseLeave = (): void => this.scheduleClose(200);

  readonly isTouch = toSignal(
    this.breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
      .pipe(map((state) => state.matches)),
    { initialValue: false }
  );

  private readonly resolveOptions = computed(
    (): UserProgressResolveOptions => ({
      countModuleOnce: this.countModuleOnceSignal(),
    })
  );

  readonly resolved = computed(() =>
    resolveProgressMinuteTotals(
      this.pathsSignal(),
      this.totalsSignal(),
      this.registeredSignal(),
      this.progressSignal(),
      this.resolveOptions()
    )
  );

  readonly breakdownRows = computed(() =>
    buildPathBreakdownRows(this.pathsSignal(), this.resolveOptions())
  );

  readonly canOpenBreakdown = computed(() => {
    if (!this.showBreakdown) {
      return false;
    }
    const r = this.resolved();
    if (this.breakdownRows().length > 0) {
      return true;
    }
    if ((this.pathsSignal()?.length ?? 0) > 0) {
      return true;
    }
    if (r.required > 0 || r.completed > 0 || r.intended > 0) {
      return true;
    }
    return r.registeredPercentage > 0 || r.progressPercentage > 0;
  });

  readonly completedBarWidth = computed(
    () => this.resolved().progressPercentage
  );

  readonly intendedBarWidth = computed(() =>
    Math.max(0, this.resolved().registeredPercentage - this.resolved().progressPercentage)
  );

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly cdr: ChangeDetectorRef,
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef
  ) {
    this.syncInputsToSignals();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['paths'] ||
      changes['totals'] ||
      changes['registeredPercentage'] ||
      changes['progressPercentage'] ||
      changes['countModuleOnce']
    ) {
      this.syncInputsToSignals();
    }
  }

  ngOnDestroy(): void {
    this.cancelCloseTimer();
    this.detachBreakdownOverlay();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.breakdownOpen()) {
      return;
    }
    const target = event.target as Node | null;
    if (!target) {
      return;
    }
    if (this.hostRef.nativeElement.contains(target)) {
      return;
    }
    if (this.overlayRef?.overlayElement.contains(target)) {
      return;
    }
    this.setBreakdownOpen(false);
  }

  onBreakdownHostEnter(): void {
    if (this.isTouch()) {
      return;
    }
    this.cancelCloseTimer();
    this.setBreakdownOpen(true);
  }

  onBreakdownHostLeave(): void {
    if (this.isTouch()) {
      return;
    }
    this.scheduleClose(200);
  }

  onBreakdownTriggerClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.canOpenBreakdown()) {
      return;
    }
    if (this.compact || this.isTouch()) {
      this.setBreakdownOpen(!this.breakdownOpen());
    }
  }

  private scheduleClose(delayMs = 120): void {
    this.cancelCloseTimer();
    this.closeTimer = setTimeout(() => this.setBreakdownOpen(false), delayMs);
  }

  private cancelCloseTimer(): void {
    if (this.closeTimer != null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  private setBreakdownOpen(open: boolean): void {
    this.breakdownOpen.set(open);
    if (open) {
      queueMicrotask(() => this.attachBreakdownOverlay());
    } else {
      this.detachBreakdownOverlay();
    }
    this.cdr.markForCheck();
  }

  private attachBreakdownOverlay(): void {
    this.detachBreakdownOverlay();
    if (!this.breakdownPanelTemplate) {
      return;
    }
    const trigger = this.hostRef.nativeElement.querySelector(
      '.progress-label-trigger'
    ) as HTMLElement | null;
    if (!trigger) {
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(trigger)
      .withPush(true)
      .withViewportMargin(8)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 4,
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetY: 4,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'progress-breakdown-overlay-pane',
    });

    const portal = new TemplatePortal(
      this.breakdownPanelTemplate,
      this.viewContainerRef
    );
    this.overlayRef.attach(portal);

    this.overlayRef.overlayElement.addEventListener(
      'mouseenter',
      this.onOverlayMouseEnter
    );
    this.overlayRef.overlayElement.addEventListener(
      'mouseleave',
      this.onOverlayMouseLeave
    );
  }

  private detachBreakdownOverlay(): void {
    if (!this.overlayRef) {
      return;
    }
    this.overlayRef.overlayElement.removeEventListener(
      'mouseenter',
      this.onOverlayMouseEnter
    );
    this.overlayRef.overlayElement.removeEventListener(
      'mouseleave',
      this.onOverlayMouseLeave
    );
    this.overlayRef.dispose();
    this.overlayRef = null;
  }

  private syncInputsToSignals(): void {
    this.pathsSignal.set(this.paths);
    this.totalsSignal.set(this.totals);
    this.registeredSignal.set(this.registeredPercentage);
    this.progressSignal.set(this.progressPercentage);
    this.countModuleOnceSignal.set(this.countModuleOnce);
  }

  formatMinutes(minutes: number): string {
    return formatMinutesShort(minutes);
  }

  breakdownTitle(): string {
    const year = this.year ?? new Date().getFullYear();
    return `Vedligeholdelse ${year}`;
  }

  percentagesHelpTooltip(): string {
    const r = this.resolved();
    if (this.aggregateContext === 'location') {
      return `Tilmeldt ${r.registeredPercentage}% · Deltaget ${r.progressPercentage}%`;
    }
    return `Tilmeldt ${r.registeredPercentage}% · Deltaget ${r.progressPercentage}%`;
  }
}
